import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { useOutletContext, useParams } from "react-router-dom";
import { db } from "../firebase/firebase";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";
import "./AgendaEditComponent.scss";

const EMPTY_SLOT = { event: "", time: "" };

const AgendaEditComponent = () => {
  const { slug } = useParams();
  const { weddingData } = useOutletContext();

  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  const [isAgendaEnabled, setIsAgendaEnabled] = useState(
    weddingData?.isAgenda !== false
  );
  const [isSavingAgendaVisibility, setIsSavingAgendaVisibility] =
    useState(false);

  const isTrialPlan =
    weddingData?.isTrial === true || weddingData?.plan === "trial";

  const canAddUnlimited = !isTrialPlan;
  const trialSlotLimit = 2;

  useEffect(() => {
    setIsAgendaEnabled(weddingData?.isAgenda !== false);
  }, [weddingData?.isAgenda]);

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);

        if (!slug) {
          if (isMounted) setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", slug));

        if (!isMounted) return;

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading site theme:", error);
        if (isMounted) {
          setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
        }
      } finally {
        if (isMounted) {
          setThemeLoading(false);
        }
      }
    };

    loadTheme();

    return () => {
      isMounted = false;
    };
  }, [slug, weddingData]);

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  const initialAgenda = useMemo(() => {
    const existingAgenda = Array.isArray(weddingData?.agenda)
      ? weddingData.agenda
      : [];

    if (canAddUnlimited) {
      return existingAgenda.length > 0
        ? existingAgenda.map((item) => ({
            event: item?.event || "",
            time: item?.time || "",
          }))
        : [{ ...EMPTY_SLOT }];
    }

    return Array.from({ length: trialSlotLimit }, (_, index) => ({
      event: existingAgenda[index]?.event || "",
      time: existingAgenda[index]?.time || "",
    }));
  }, [weddingData, canAddUnlimited]);

  const [agendaSlots, setAgendaSlots] = useState(initialAgenda);
  const [editingIndex, setEditingIndex] = useState(null);
  const [draftValues, setDraftValues] = useState(EMPTY_SLOT);
  const [loading, setLoading] = useState(false);
  const [savingIndex, setSavingIndex] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setAgendaSlots(initialAgenda);
  }, [initialAgenda]);

  const getUserDocId = () => {
    return (
      weddingData?.ownerUid ||
      weddingData?.uid ||
      weddingData?.userId ||
      weddingData?.id ||
      ""
    );
  };

  const handleToggleAgendaVisibility = async () => {
    const nextValue = !isAgendaEnabled;

    setSuccessMessage("");
    setErrorMessage("");
    setIsSavingAgendaVisibility(true);
    setIsAgendaEnabled(nextValue);

    try {
      const payload = {
        isAgenda: nextValue,
        updatedAt: serverTimestamp(),
      };

      const updateTasks = [];

      if (slug) {
        updateTasks.push(updateDoc(doc(db, "sites", slug), payload));
      }

      const userDocId = getUserDocId();

      if (userDocId) {
        updateTasks.push(updateDoc(doc(db, "users", userDocId), payload));
      }

      if (updateTasks.length === 0) {
        throw new Error("Missing site slug or user document id.");
      }

      await Promise.all(updateTasks);

      setSuccessMessage(
        nextValue
          ? "Agenda section enabled successfully."
          : "Agenda section hidden successfully."
      );
    } catch (error) {
      console.error("Agenda visibility update error:", error);
      setIsAgendaEnabled(!nextValue);
      setErrorMessage("Failed to update agenda visibility. Please try again.");
    } finally {
      setIsSavingAgendaVisibility(false);
    }
  };

  const persistAgenda = async (
    slots,
    successText = "Agenda updated successfully."
  ) => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const cleanedAgenda = slots
        .map((slot, index) => ({
          id: index + 1,
          event: slot.event.trim(),
          time: slot.time,
        }))
        .filter((slot) => slot.event || slot.time);

      await updateDoc(doc(db, "sites", slug), {
        agenda: cleanedAgenda,
        updatedAt: serverTimestamp(),
      });

      setSuccessMessage(successText);
    } catch (error) {
      console.error("Agenda update error:", error);
      setErrorMessage("Failed to update agenda. Please try again.");
      throw error;
    } finally {
      setLoading(false);
      setSavingIndex(null);
    }
  };

  const handleAddOrEditClick = (index) => {
    setSuccessMessage("");
    setErrorMessage("");
    setEditingIndex(index);
    setDraftValues({
      event: agendaSlots[index]?.event || "",
      time: agendaSlots[index]?.time || "",
    });
  };

  const handleDraftChange = (e) => {
    const { name, value } = e.target;

    setDraftValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveSlot = async (index) => {
    setSuccessMessage("");
    setErrorMessage("");

    if (!draftValues.event.trim()) {
      setErrorMessage(`Please enter an event for item ${index + 1}.`);
      return;
    }

    if (!draftValues.time) {
      setErrorMessage(`Please enter a time for item ${index + 1}.`);
      return;
    }

    const updatedSlots = [...agendaSlots];
    updatedSlots[index] = {
      event: draftValues.event.trim(),
      time: draftValues.time,
    };

    setSavingIndex(index);
    setAgendaSlots(updatedSlots);

    try {
      await persistAgenda(updatedSlots, `Event ${index + 1} saved successfully.`);
      setEditingIndex(null);
      setDraftValues(EMPTY_SLOT);
    } catch {
      // handled in persistAgenda
    }
  };

  const handleDeleteSlot = async (index) => {
    setSuccessMessage("");
    setErrorMessage("");

    let updatedSlots = [];

    if (!canAddUnlimited) {
      updatedSlots = [...agendaSlots];
      updatedSlots[index] = { ...EMPTY_SLOT };
    } else {
      updatedSlots = agendaSlots.filter((_, itemIndex) => itemIndex !== index);

      if (updatedSlots.length === 0) {
        updatedSlots = [{ ...EMPTY_SLOT }];
      }
    }

    setSavingIndex(index);
    setAgendaSlots(updatedSlots);

    if (editingIndex === index) {
      setEditingIndex(null);
      setDraftValues(EMPTY_SLOT);
    }

    try {
      await persistAgenda(updatedSlots, `Event ${index + 1} deleted successfully.`);
    } catch {
      // handled in persistAgenda
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setDraftValues(EMPTY_SLOT);
    setErrorMessage("");
  };

  const handleAddNewEvent = () => {
    setSuccessMessage("");
    setErrorMessage("");

    if (!canAddUnlimited) {
      setErrorMessage("Only premium users can add unlimited agenda events.");
      return;
    }

    const updatedSlots = [...agendaSlots, { ...EMPTY_SLOT }];
    setAgendaSlots(updatedSlots);
    setEditingIndex(updatedSlots.length - 1);
    setDraftValues(EMPTY_SLOT);

    setTimeout(() => {
      const element = document.getElementById(`event-${updatedSlots.length - 1}`);
      if (element) element.focus();
    }, 0);
  };

  if (themeLoading || !siteTheme) return null;

  return (
    <div className="agendaEdit" style={themeStyle}>
      <div className="agendaEdit__card">
        <div className="agendaEdit__header">
          <div>
            <p className="agendaEdit__eyebrow">Agenda</p>
            <h1 className="agendaEdit__title">Edit My Agenda</h1>
            <p className="agendaEdit__subtitle">
              Try to add the most significant moments in your event.
            </p>
          </div>

          <div className="agendaEdit__visibilityBox">
            <div className="agendaEdit__visibilityText">
              <span className="agendaEdit__visibilityTitle">Agenda Section</span>
              <span className="agendaEdit__visibilityStatus">
                {isAgendaEnabled ? "Visible on invitation" : "Hidden from invitation"}
              </span>
            </div>

            <button
              type="button"
              className={`agendaEdit__switch ${
                isAgendaEnabled ? "agendaEdit__switch--active" : ""
              }`}
              onClick={handleToggleAgendaVisibility}
              disabled={isSavingAgendaVisibility || loading}
              aria-pressed={isAgendaEnabled}
              aria-label="Toggle agenda section visibility"
            >
              <span className="agendaEdit__switchKnob" />
            </button>
          </div>
        </div>

        <div className="agendaEdit__noticeWrap">
          <div className="agendaEdit__notice agendaEdit__notice--info">
            <strong>Premium feature:</strong> Premium users can add unlimited
            agenda events. Trial users can add only 2 events.
          </div>

          {!canAddUnlimited && (
            <div className="agendaEdit__notice agendaEdit__notice--soft">
              You are currently on the trial plan. You can edit up to 2 agenda
              events only.
            </div>
          )}
        </div>

        <div className="agendaEdit__list">
          {agendaSlots.map((slot, index) => {
            const isEditing = editingIndex === index;
            const hasValue = slot.event || slot.time;
            const isSavingThis = savingIndex === index && loading;

            return (
              <div className="agendaEdit__item" key={index}>
                <div className="agendaEdit__itemTop">
                  <div className="agendaEdit__number">{index + 1}</div>

                  {!isEditing ? (
                    <div className="agendaEdit__display">
                      <div className="agendaEdit__displayRow">
                        <span className="agendaEdit__label">Event</span>
                        <span
                          className={`agendaEdit__value ${
                            !slot.event ? "is-empty" : ""
                          }`}
                        >
                          {slot.event || "No event added"}
                        </span>
                      </div>

                      <div className="agendaEdit__displayRow">
                        <span className="agendaEdit__label">Time</span>
                        <span
                          className={`agendaEdit__value ${
                            !slot.time ? "is-empty" : ""
                          }`}
                        >
                          {slot.time || "No time added"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="agendaEdit__form">
                      <div className="agendaEdit__field">
                        <label
                          className="agendaEdit__fieldLabel"
                          htmlFor={`event-${index}`}
                        >
                          Event
                        </label>
                        <input
                          id={`event-${index}`}
                          name="event"
                          type="text"
                          className="agendaEdit__input"
                          placeholder="Enter event"
                          value={draftValues.event}
                          onChange={handleDraftChange}
                          disabled={loading}
                          autoCorrect="off"
                        />
                      </div>

                      <div className="agendaEdit__field">
                        <label
                          className="agendaEdit__fieldLabel"
                          htmlFor={`time-${index}`}
                        >
                          Time
                        </label>
                        <input
                          id={`time-${index}`}
                          name="time"
                          type="time"
                          className="agendaEdit__input"
                          value={draftValues.time}
                          onChange={handleDraftChange}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="agendaEdit__actions">
                  {!isEditing ? (
                    <>
                      <button
                        type="button"
                        className="agendaEdit__btn agendaEdit__btn--edit"
                        onClick={() => handleAddOrEditClick(index)}
                        disabled={loading}
                      >
                        {hasValue ? "Edit" : "Add"}
                      </button>

                      <button
                        type="button"
                        className="agendaEdit__btn agendaEdit__btn--delete"
                        onClick={() => handleDeleteSlot(index)}
                        disabled={!hasValue || loading}
                      >
                        {isSavingThis ? "Deleting..." : "Delete"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="agendaEdit__btn agendaEdit__btn--save"
                        onClick={() => handleSaveSlot(index)}
                        disabled={loading}
                      >
                        {isSavingThis ? "Saving..." : "Save Slot"}
                      </button>

                      <button
                        type="button"
                        className="agendaEdit__btn agendaEdit__btn--cancel"
                        onClick={handleCancelEdit}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {errorMessage && (
          <p className="agendaEdit__message agendaEdit__message--error">
            {errorMessage}
          </p>
        )}

        {successMessage && (
          <p className="agendaEdit__message agendaEdit__message--success">
            {successMessage}
          </p>
        )}

        <div className="agendaEdit__footer">
          <button
            type="button"
            className="agendaEdit__addNewBtn"
            onClick={handleAddNewEvent}
            disabled={!canAddUnlimited || loading}
          >
            Add New Event
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgendaEditComponent;